import type { ReactNode } from 'react';
import { fieldId } from '../format';
import { Html } from '../Html';
import { AlertIcon } from '../icons';

export interface FieldProps {
  /** API-shaped field key, e.g. `user.email` or `custom_fields.255` */
  fieldKey: string;
  label: string;
  /** label may contain tenant HTML (custom checkbox labels) */
  labelHtml?: boolean;
  required?: boolean;
  help?: string | null;
  errors?: string[];
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
  /** render the label as a fieldset legend (groups) */
  group?: boolean;
  className?: string;
}

/** Label + control + help/error wiring (aria-describedby, aria-invalid) for any field. */
export function Field({ fieldKey, label, labelHtml, required, help, errors = [], children, group, className }: FieldProps) {
  const id = fieldId(fieldKey);
  const helpId = help ? `${id}-help` : undefined;
  const errId = errors.length ? `${id}-error` : undefined;
  const describedBy = [errId, helpId].filter(Boolean).join(' ') || undefined;
  const labelNode = (
    <>
      {labelHtml ? <Html html={label} /> : label}
      {required ? (
        <span className="cbf-req" aria-hidden="true">
          {' '}
          *
        </span>
      ) : null}
      {required ? <span className="cbf-sr"> (required)</span> : null}
    </>
  );
  const body = (
    <>
      {group ? <legend className="cbf-label">{labelNode}</legend> : <label className="cbf-label" htmlFor={id}>{labelNode}</label>}
      {children({ id, describedBy, invalid: errors.length > 0 })}
      {errors.length ? (
        <p className="cbf-error" id={errId} role="alert">
          <AlertIcon />
          <span>{errors.join(' ')}</span>
        </p>
      ) : help ? (
        <p className="cbf-help" id={helpId}>
          {help}
        </p>
      ) : null}
    </>
  );
  const cls = ['cbf-field', className].filter(Boolean).join(' ');
  return group ? (
    <fieldset className={cls} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }} data-cbf-field={fieldKey}>
      {body}
    </fieldset>
  ) : (
    <div className={cls} data-cbf-field={fieldKey}>
      {body}
    </div>
  );
}
